import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { translate, postKmsData, baseUrl, HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import { PreviewPlaceholder, StyledBox, PreviewLabel, VideoTitle } from "./StyleComponents";
import { ComposedInput, Tag, Typography } from "@kaltura/ds-react-components";
import { TextFieldLoading } from "@kaltura/mediaspace-shared-ui/form";
import YouTubePreview from "./YouTubePreview";
import { YouTubeVideoData, saveResponse } from "./types";
import { fetchYouTubeVideoInfo } from "../services/youtubeService";
import { YouTubeImportModalState } from "./types";
import { KalturaExternalMediaEntry } from "kaltura-typescript-client/api/types/KalturaExternalMediaEntry";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface YouTubeImportFormProps {
    onImport: (youtubeEntry: KalturaExternalMediaEntry) => void;
    initialState?: YouTubeImportModalState;
    uiconfId?: string;
    rootClassName?: string;
    onDataChange?: (data: { videoData: YouTubeVideoData | null; isLoading: boolean; isImporting: boolean }) => void;
    formId: string;
    onError?: (message: string) => void;
}

/*
 * YouTubeImportForm is a reusable component that manages the state and logic for YouTube video import.
 * It handles YouTube URL input, validation, preview fetching, and import functionality.
 * The component can be used standalone or within modal wrappers, making it flexible for different contexts.
 */
const YouTubeImportForm: React.FC<YouTubeImportFormProps> = ({
    onImport,
    initialState,
    uiconfId,
    rootClassName = "youtube-import-modal",
    onDataChange,
    formId,
    onError,
}) => {
    // Use initialState if provided, otherwise use default values
    const [youtubeUrl, setYoutubeUrl] = useState(initialState?.youtubeUrl || "");
    const [isLoading, setIsLoading] = useState(initialState?.isLoading || false);
    const [error, setError] = useState<string | undefined>(initialState?.error);
    const [videoData, setVideoData] = useState<YouTubeVideoData | null>(initialState?.videoData || null);
    const [isImporting, setIsImporting] = useState(false);
    const sendButtonAnalytics = useButtonAnalytics();

    const config = useConfig();
    const { partnerId, cdnUrl } = config.application;

    const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setYoutubeUrl(event.target.value);
        setVideoData(null);
        sendButtonAnalytics("Search for YouTube video", ButtonClickAnalyticsType.SEARCH);
    };

    const validateYoutubeUrl = (url: string): boolean => {
        // Basic validation for YouTube URLs
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
        return youtubeRegex.test(url);
    };

    const videoId = useMemo(() => {
        if (!youtubeUrl) return "";
        return (
            youtubeUrl.match(
                /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
            )?.[1] || ""
        );
    }, [youtubeUrl]);

    const fetchYoutubeData = useCallback(async () => {
        if (!youtubeUrl || youtubeUrl === "") {
            return;
        }

        if (!validateYoutubeUrl(youtubeUrl)) {
            setError(translate("URL is not valid"));
            return;
        }

        setIsLoading(true);
        setError(undefined);

        try {
            // Extract video ID from URL
            if (videoId === "") {
                setError(translate("URL is not valid"));
                setIsLoading(false);
                return;
            }

            // Use the YouTube API service to fetch video data
            const videoData = await fetchYouTubeVideoInfo(videoId);

            // Update the URL to match the input URL
            videoData.url = youtubeUrl;

            setVideoData(videoData);
            setIsLoading(false);
        }
        catch (err) {
            setError(translate("Failed to load video data"));
            setIsLoading(false);
        }
    }, [youtubeUrl, videoId]);

    const handleImport = async () => {
        if (!videoData) return;

        setIsImporting(true);

        //compose a comma-delimited string from the videoData.tags array
        const tags = videoData.tags ? videoData.tags.join(",") : "";
        try {
            const result: saveResponse = await postKmsData(
                `${baseUrl}/youtube/api/save`,
                {
                    name: videoData.title,
                    description: videoData.description,
                    youtubeReferenceId: videoData.id,
                    tags: tags,
                }
            );

            if (result.result && result.result.id) {
                onImport(result.result);
            }
            else {
                console.error("Import failed:", result?.error || "Unknown error");
                onError?.(translate("Importing of the youtube entry failed"));
            }
        }
        catch (error) {
            console.error("Import error:", error);
            onError?.(translate("Importing of the youtube entry failed"));
        }
        finally {
            setIsImporting(false);
        }
    };

    useEffect(() => {
        fetchYoutubeData();
    }, [youtubeUrl, fetchYoutubeData]);

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        sendButtonAnalytics("Import from YouTube", ButtonClickAnalyticsType.CREATE);
        handleImport();
    };

    const handleDataChange = useEventHandler(onDataChange);
    // Notify parent component of data changes if callback is provided
    useEffect(() => {
        handleDataChange?.({ videoData, isLoading, isImporting });
    }, [videoData, isLoading, isImporting, handleDataChange]);

    return (
        <form id={formId} onSubmit={handleFormSubmit}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <ComposedInput
                    error={!!error}
                    formHelperText={error}
                    value={youtubeUrl}
                    inputLabel={"Enter URL or ID"}
                    inputAdditionalInfo={translate("Only public videos are supported")}
                    placeholder={"e.g. https://www.youtube.com/watch?v=CNRavTdSN5E"}
                    fullWidth={true}
                    data-testid={rootClassName + "-url-input"}
                    onChange={handleUrlChange}
                    endAdornment={isLoading ? <TextFieldLoading /> : null}
                />
            </div>
            <StyledBox>
                {videoData && !isLoading ? (
                    <>
                        <YouTubePreview
                            videoData={videoData}
                            partnerId={partnerId}
                            uiconfId={uiconfId}
                            cdnUrl={cdnUrl}
                        />
                        <VideoTitle variant="body1" kuid={rootClassName + "-title"}>
                            {videoData.title}
                        </VideoTitle>
                        {videoData.description && (
                            <StyledBox>
                                <Typography variant="body2" kuid={rootClassName + "-description"}>
                                    {HtmlReactParser(videoData.description)}
                                </Typography>
                            </StyledBox>
                        )}
                        {videoData.tags && videoData.tags.length > 0 && (
                            <StyledBox data-testid={rootClassName + "-tags"}>
                                {videoData.tags.map((tag: string, index: number) => (
                                    <Tag key={index} label={tag} />
                                ))}
                            </StyledBox>
                        )}
                    </>
                ) : (
                    <PreviewPlaceholder data-testid={rootClassName + "-preview-placeholder"}>
                        <PreviewLabel variant="body2">{isLoading ? "Creating preview" : "Preview"}</PreviewLabel>
                    </PreviewPlaceholder>
                )}
            </StyledBox>
        </form>
    );
};

export default YouTubeImportForm;
